import { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  Descriptions,
  Flex,
  message,
  Select,
  Tag,
  Timeline,
  Typography,
  Image
} from 'antd';
import { useTranslation } from 'react-i18next';
import { GENDERS_VALUES } from 'constants/users';
import dayjs from 'dayjs';
import { DATE_FORMAT, FULL_DATE_FORMAT } from 'constants/dates';
import {
  FAST_APPOINTMENTS_COLOR,
  FAST_APPOINTMENTS_LABEL,
  FAST_APPOINTMENTS_STATUS,
  FastAppointmentValues
} from 'constants/appointments';
import { useUIDispatchContext } from 'providers/UI';
import { fetchOrganizations } from 'services/api/organizations';
import { OrganizationListRow } from 'types/api/organizations';
import {
  fetchFastAppointmentById,
  updateFastAppointmentOrganization,
  updateFastAppointmentStatus
} from 'services/api/fastAppointments';
import {
  FastAppointmentDetail,
  UpdateFastAppointmentStatusInput
} from 'types/api/fastAppointments';
import { PERMISSION_ACTIONS, PERMISSION_ROUTES } from 'constants/permissions';
import { usePermission } from 'hooks';

import useOrgEffect from 'hooks/useOrgEffect';
import { Loading, PageWrapper } from 'components/ui';
import { parseJson } from 'utils/json';
import { getFileUrl } from 'utils/file';
import { showErrorMessage } from 'utils/showErrorMessage';
import { Private } from 'components/shared';

const { Text } = Typography;

const PatientRequestsDetails: FC = () => {
  const { toggleModal } = useUIDispatchContext();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();

  const { checkUserPermission } = usePermission();

  const [patientRequest, setPatientRequest] =
    useState<FastAppointmentDetail | null>(null);

  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const refetch = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  useOrgEffect(() => {
    if (!params.id) return;
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchFastAppointmentById(params.id as string);
        if (!cancelled) setPatientRequest(res);
      } catch (err) {
        if (!cancelled) showErrorMessage(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [params.id, refreshKey]);

  const [organizationsList, setOrganizationsList] = useState<
    OrganizationListRow[]
  >([]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const rows = await fetchOrganizations();
        if (!cancelled) setOrganizationsList(rows);
      } catch {
        // swallow — empty org options on failure
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  const onStatusSubmit = async (values: {
    note: string;
    status: UpdateFastAppointmentStatusInput['status'];
  }) => {
    if (!params.id) return;

    try {
      await updateFastAppointmentStatus(params.id, {
        status: values.status,
        note: values.note
      });
      toggleModal('updateStatus', false);
      refetch();
    } catch (err) {
      showErrorMessage(err);
    }
  };

  const [isUpdating, setIsUpdating] = useState(false);

  const updateAppointmentOrganization = useCallback(
    async (appointmentId: string | number, organizationId: number) => {
      try {
        setIsUpdating(true);
        await updateFastAppointmentOrganization(appointmentId, organizationId);
        message.success(t('Organization updated successfully'));
        refetch();
      } catch (err) {
        showErrorMessage(err);
      } finally {
        setIsUpdating(false);
      }
    },
    [refetch, t]
  );

  const patientRequestId = patientRequest;
  const files = parseJson(patientRequestId?.files) || [];

  if (!patientRequestId || loading) {
    return <Loading />;
  }

  const selectedOrganization = organizationsList.filter(item =>
    patientRequestId?.organization_info?.includes(String(item.id))
  );

  const patientOrganization = organizationsList.find(
    item => String(item.id) === String(patientRequestId?.organization_id)
  );

  const hasFileViewPermission = checkUserPermission(
    PERMISSION_ROUTES.patientRequests,
    PERMISSION_ACTIONS.viewFile
  );

  return (
    <PageWrapper
      back
      onClickBack={() => navigate(-1)}
      title={`Patient Request: #${params.id}`}
      extra={[
        <Private
          key="change-status-button"
          route={PERMISSION_ROUTES.patientRequests}
          action={PERMISSION_ACTIONS.update}
        >
          <Button
            type="primary"
            onClick={() =>
              toggleModal('updateStatus', true, {
                id: params.id,
                currentStatus: `${patientRequestId.status}`,
                statusList: FAST_APPOINTMENTS_LABEL,
                onSubmit: onStatusSubmit
              })
            }
          >
            Change status
          </Button>
        </Private>
      ]}
    >
      <Card title="Personal Information" style={{ marginBottom: 24 }}>
        <Descriptions labelStyle={{ fontWeight: 'bold' }}>
          <Descriptions.Item label="User Name">
            {patientRequestId.first_name} {patientRequestId.last_name}
          </Descriptions.Item>
          <Descriptions.Item label="Phone number">
            <a href={`tel:${patientRequestId.phone}`}>
              {patientRequestId.phone}
            </a>
          </Descriptions.Item>
          <Descriptions.Item label="Email">
            <a href={`mailto:${patientRequestId.email}`}>
              {patientRequestId.email}
            </a>
          </Descriptions.Item>
          <Descriptions.Item label="Gender">
            {t(
              `appointments_details.${
                GENDERS_VALUES[
                  patientRequestId.gender as keyof typeof GENDERS_VALUES
                ]
              }`
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Date of birth">
            {dayjs(patientRequestId.date_of_birth).format(DATE_FORMAT)}
          </Descriptions.Item>
          <Descriptions.Item label="Created at">
            {dayjs(patientRequestId.created_at).format(FULL_DATE_FORMAT)}
          </Descriptions.Item>
        </Descriptions>
      </Card>
      <Card title="Appointment Details" style={{ marginBottom: 24 }}>
        <Descriptions
          layout="vertical"
          column={2}
          contentStyle={{ marginBottom: 12 }}
          labelStyle={{ fontWeight: 'bold' }}
        >
          <Descriptions.Item label="Health Concerns">
            {patientRequestId.note}
          </Descriptions.Item>
          {!!selectedOrganization?.length && (
            <Descriptions.Item label="Selected Organizations">
              <Flex gap={24}>
                {selectedOrganization.map(item => {
                  return (
                    <Flex gap={6} key={item.id}>
                      <Image
                        src={getFileUrl(item.image || '')}
                        width={24}
                        height={24}
                        preview={false}
                        style={{
                          borderRadius: '50%',
                          objectFit: 'cover'
                        }}
                      />
                      <Text>{item.name?.en ?? ''}</Text>
                    </Flex>
                  );
                })}
              </Flex>
            </Descriptions.Item>
          )}
          {hasFileViewPermission && (
            <Descriptions.Item label="Files" span={1}>
              {files.length ? (
                <ul>
                  {files.map((file: string, key: number) => (
                    <li key={`file-${file}-${key}`}>
                      <a
                        href={getFileUrl(file, 150, patientRequest.user)}
                        download={file}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {file}
                      </a>
                    </li>
                  ))}
                </ul>
              ) : (
                '-'
              )}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Organization">
            <Select
              labelInValue
              placeholder="Choose organization"
              style={{ width: 200 }}
              allowClear
              loading={isUpdating}
              value={
                patientOrganization
                  ? {
                      value: patientOrganization.id,
                      label: patientOrganization.name?.en ?? ''
                    }
                  : null
              }
              onChange={e => {
                if (!params.id || !e) return;
                updateAppointmentOrganization(params.id, Number(e.value));
              }}
              options={
                selectedOrganization?.length
                  ? selectedOrganization.map(item => ({
                      value: item.id,
                      label: item.name?.en ?? ''
                    }))
                  : organizationsList.map(item => ({
                      value: item.id,
                      label: item.name?.en ?? ''
                    }))
              }
            />
          </Descriptions.Item>
        </Descriptions>
      </Card>
      <Card title="History">
        <Timeline
          items={[
            ...(Array.isArray(patientRequestId.notes)
              ? patientRequestId.notes.map(item => ({
                  children: (
                    <>
                      <p key={`history-${item.id}`}>
                        Status changed to{' '}
                        <Tag
                          color={
                            FAST_APPOINTMENTS_COLOR[
                              item.status as FastAppointmentValues
                            ]
                          }
                        >
                          {
                            FAST_APPOINTMENTS_LABEL[
                              item.status as FastAppointmentValues
                            ]
                          }
                        </Tag>
                        on{' '}
                        <b>{dayjs(item.created_at).format(FULL_DATE_FORMAT)}</b>
                      </p>
                      <p style={{ fontWeight: 500 }}>{item.note}</p>
                    </>
                  )
                }))
              : []),
            {
              children: (
                <p>
                  Request created with a status{' '}
                  <Tag
                    color={
                      FAST_APPOINTMENTS_COLOR[FAST_APPOINTMENTS_STATUS.new]
                    }
                  >
                    {FAST_APPOINTMENTS_LABEL[FAST_APPOINTMENTS_STATUS.new]}
                  </Tag>
                  on{' '}
                  <b>
                    {dayjs(patientRequestId.created_at).format(
                      FULL_DATE_FORMAT
                    )}
                  </b>
                </p>
              )
            }
          ]}
        />
      </Card>
    </PageWrapper>
  );
};

export default PatientRequestsDetails;
